import { ApiError } from "@/lib/api"
import { formatCurrency, formatPercent } from "@/lib/utils"
import { useQuery } from "@tanstack/react-query"
import { type Fatia, Rosca } from "./Rosca"
import { Barras, Secao } from "./blocos"
import { fetchMargem } from "./margem"

// As faixas de margem tomada, na ordem de quem tem mais folga para quem não tem nenhuma. A cor
// segue o código da página: verde é folga, âmbar é aperto que ainda cabe um crédito pequeno,
// vermelho é quem não cabe parcela nova nenhuma.
const FAIXAS: Record<string, Omit<Fatia, "valor">> = {
    free: {
        rotulo: "Margem livre",
        tom: "ok",
        explicacao: "Nada comprometido: podem contratar até o limite.",
    },
    under_half: {
        rotulo: "Até metade tomada",
        tom: "dado",
        explicacao: "Ainda têm a maior parte da margem.",
    },
    over_half: {
        rotulo: "Mais da metade tomada",
        tom: "off",
        explicacao: "Sobra pouco para um crédito novo.",
    },
    exhausted: {
        rotulo: "Margem esgotada",
        tom: "stop",
        explicacao: "Não cabe nenhuma parcela nova na folha.",
    },
}

const MULTIPLO = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 })

const pessoas = (n: number) => (n === 1 ? "1 pessoa" : `${n} pessoas`)

const Numero = ({ rotulo, valor, nota }: { rotulo: string; valor: string; nota: string }) => (
    <div>
        <p className="text-xs font-semibold text-soft">{rotulo}</p>
        <p className="mt-0.5 font-num text-xl font-bold tabular-nums text-ink">{valor}</p>
        <p className="text-xs text-faint">{nota}</p>
    </div>
)

// Busca própria, e não junto com o resto da página: a margem vem do townhall, e o townhall fora
// do ar não pode esconder do RH o que o leilão já sabe. A seção falha sozinha, dentro dela.
export const MargemDaFolha = () => {
    const { data, isPending, error, refetch } = useQuery({
        queryKey: ["margem"],
        queryFn: fetchMargem,
        retry: (tentativas, erro) =>
            erro instanceof ApiError &&
            (erro.falha === "rede" || erro.falha === "servidor") &&
            tentativas < 2,
    })

    const titulo = "Margem da folha"
    const descricao = "Quanto da margem consignável dos seus colaboradores já está tomada"

    if (isPending) {
        return (
            <Secao titulo={titulo} descricao={descricao} larga>
                <div
                    aria-busy="true"
                    aria-label="Carregando a margem da folha"
                    className="flex animate-pulse items-center gap-5 motion-reduce:animate-none"
                >
                    <span className="block h-40 w-40 shrink-0 rounded-full bg-sunk" />
                    <span className="block h-24 w-full rounded bg-sunk" />
                </div>
            </Secao>
        )
    }

    if (error) {
        return (
            <Secao titulo={titulo} descricao={descricao} larga>
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-sunk p-4 text-sm text-soft">
                    <p>
                        Não conseguimos carregar a margem agora. O resto da página não depende dela.
                    </p>
                    <button
                        type="button"
                        onClick={() => void refetch()}
                        className="rounded-full bg-brand px-4 py-2 font-semibold text-brand-ink transition-colors hover:bg-brand-strong"
                    >
                        Tentar de novo
                    </button>
                </div>
            </Secao>
        )
    }

    const { margin, people, ineligibility } = data

    if (!margin.people_with_data) {
        return (
            <Secao titulo={titulo} descricao={descricao} larga>
                <p className="text-sm text-soft">
                    Ninguém da sua empresa consultou a margem ainda. Quando a primeira consulta
                    entrar, esta seção mostra quanto da folha já está comprometido.
                </p>
            </Secao>
        )
    }

    // Só as faixas que a tela conhece: uma faixa nova no serviço aparece quando a tela aprender a
    // nomeá-la, e não como uma fatia sem rótulo.
    const fatias: Fatia[] = margin.usage.flatMap((faixa) => {
        const conhecida = FAIXAS[faixa.usage]
        return conhecida ? [{ ...conhecida, valor: faixa.people }] : []
    })

    return (
        <Secao titulo={titulo} descricao={descricao} larga>
            <Rosca
                fatias={fatias}
                centro={
                    margin.committed_share == null ? "—" : formatPercent(margin.committed_share)
                }
                nota="comprometida"
            />

            <div className="mt-6 grid gap-4 border-t border-line pt-5 sm:grid-cols-3">
                <Numero
                    rotulo="Margem legal da folha"
                    valor={formatCurrency(margin.legal_margin_total)}
                    nota="35% da renda-base, somando todos"
                />
                <Numero
                    rotulo="Já comprometida"
                    valor={formatCurrency(margin.committed_total)}
                    nota={`em parcelas de consignado, de ${pessoas(margin.people_with_data)}`}
                />
                <Numero
                    rotulo="Dívida consignada"
                    valor={
                        margin.debt_to_base == null
                            ? "—"
                            : `${MULTIPLO.format(margin.debt_to_base)}×`
                    }
                    nota="a renda-base mensal, em saldo devedor"
                />
            </div>

            {ineligibility.length > 0 && (
                <div className="mt-6 border-t border-line pt-5">
                    <h3 className="font-display text-sm font-semibold text-ink">
                        Por que {pessoas(people.ineligible)}{" "}
                        {people.ineligible === 1 ? "não pôde" : "não puderam"} nem simular
                    </h3>
                    <p className="mb-3 mt-0.5 text-sm text-faint">Resposta da consulta de margem</p>
                    <Barras
                        rotulosLongos
                        linhas={ineligibility.map((motivo) => ({
                            rotulo: motivo.reason,
                            valor: motivo.people,
                            tom: "stop",
                        }))}
                    />
                </div>
            )}
        </Secao>
    )
}
